/**
 * CallRecorder - Recording and transcription management for PeersCaller
 * Handles local recording of mixed streams and audio transcription
 */

import MultiStreamsMixer from "multistreamsmixer";
import Transcriber from "@sawport/transcriber";
import type { RecordingConfig, RecordingData, TranscriptData } from "../types";

export interface RecorderCallbacks {
  onRecordingStarted?: (recordingData: RecordingData) => void;
  onRecordingChunk?: (chunk: Blob, recordingData: RecordingData) => void;
  onRecordingStopped?: (recordingData: RecordingData) => void;
  onTranscriptReceived?: (transcript: TranscriptData) => void;
  onError?: (error: Error) => void;
}

export class CallRecorder {
  private mixer: MultiStreamsMixer | null = null;
  private recorder: MediaRecorder | null = null;
  private localTranscriber: Transcriber | null = null;
  private isRecording = false;

  private config: RecordingConfig;
  private callbacks: RecorderCallbacks;

  constructor(
    conversationId: string,
    userId: string,
    config: RecordingConfig = {},
    callbacks: RecorderCallbacks = {}
  ) {
    this.config = {
      mimeType: "video/webm;codecs=vp8,opus",
      videoBitsPerSecond: 1000000, // 1Mbps
      audioBitsPerSecond: 128000, // 128kbps
      interval: 1000, // 1 second chunks
      ...config,
    };
    this.callbacks = callbacks;

    // Store for potential future use
    console.log(
      `[CallRecorder] Initialized for conversation ${conversationId}, user ${userId}`
    );
  }

  /**
   * Start recording with mixed streams and recording data
   */
  async startRecording(
    streams: MediaStream[],
    recordingData: RecordingData
  ): Promise<void> {
    try {
      console.log(
        "[CallRecorder] Starting recording with",
        streams.length,
        "streams"
      );

      if (this.isRecording) {
        throw new Error("Recording already in progress");
      }

      if (!recordingData) {
        throw new Error("Recording data is required");
      }

      // Create mixer with streams
      this.mixer = new MultiStreamsMixer(streams);
      this.mixer.frameInterval = 1;
      this.mixer.startDrawingFrames();

      // Create transcriber for local stream (first stream assumed to be local)
      const localStream = streams[0];
      if (localStream && localStream.getAudioTracks().length > 0) {
        this.localTranscriber = new Transcriber(localStream);
      }

      // Check if MediaRecorder is supported
      if (!MediaRecorder.isTypeSupported(this.config.mimeType!)) {
        console.warn(
          "[CallRecorder] Preferred mime type not supported, falling back to default"
        );
        this.config.mimeType = "video/webm";
      }

      // Get mixed stream
      const mixedStream = this.mixer.getMixedStream();

      // Create MediaRecorder
      this.recorder = new MediaRecorder(mixedStream, {
        mimeType: this.config.mimeType,
        videoBitsPerSecond: this.config.videoBitsPerSecond,
        audioBitsPerSecond: this.config.audioBitsPerSecond,
      });

      // Set up event handlers
      this.setupRecorderEventHandlers(recordingData);

      // Start recording
      this.recorder.start(this.config.interval);
      this.isRecording = true;

      // Start transcription if enabled and transcriber is available
      if (this.localTranscriber) {
        this.localTranscriber.start();
      }

      this.callbacks.onRecordingStarted?.(recordingData);
      console.log("[CallRecorder] Recording started successfully");
    } catch (error) {
      console.error("[CallRecorder] Failed to start recording:", error);
      this.callbacks.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Stop recording
   */
  async stopRecording(): Promise<TranscriptData[]> {
    try {
      console.log("[CallRecorder] Stopping recording");

      if (!this.isRecording || !this.recorder) {
        console.warn("[CallRecorder] No active recording to stop");
        return [];
      }

      // Stop MediaRecorder
      this.recorder.stop();
      this.isRecording = false;

      // Stop transcription and get transcripts
      let transcripts: TranscriptData[] = [];
      if (this.localTranscriber) {
        this.localTranscriber.stop();
        transcripts = this.localTranscriber.transcripts || [];
      }

      // Release mixer streams
      if (this.mixer) {
        this.mixer.releaseStreams();
      }

      console.log("[CallRecorder] Recording stopped successfully");
      return transcripts;
    } catch (error) {
      console.error("[CallRecorder] Failed to stop recording:", error);
      this.callbacks.onError?.(error as Error);
      return [];
    }
  }

  /**
   * Reset video streams for mixer (used when screen sharing changes)
   */
  resetVideoStreams(streams: MediaStream[]): void {
    if (this.mixer) {
      this.mixer.resetVideoStreams(streams);
      console.log("[CallRecorder] Video streams reset");
    }
  }

  /**
   * Set up MediaRecorder event handlers
   */
  private setupRecorderEventHandlers(recordingData: RecordingData): void {
    if (!this.recorder) return;

    this.recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.callbacks.onRecordingChunk?.(event.data, recordingData);
      }
    };

    this.recorder.onstart = () => {
      console.log("[CallRecorder] MediaRecorder started");
    };

    this.recorder.onstop = () => {
      console.log("[CallRecorder] MediaRecorder stopped");
      this.callbacks.onRecordingStopped?.(recordingData);
    };

    this.recorder.onerror = (event) => {
      console.error("[CallRecorder] MediaRecorder error:", event);
      this.callbacks.onError?.(new Error(`Recording error: ${event}`));
    };
  }

  /**
   * Get current recording state
   */
  getRecordingState(): {
    isRecording: boolean;
    isTranscribing: boolean;
    transcriptCount: number;
  } {
    return {
      isRecording: this.isRecording,
      isTranscribing: this.localTranscriber !== null,
      transcriptCount: this.localTranscriber?.transcripts?.length || 0,
    };
  }

  /**
   * Get all transcripts
   */
  getTranscripts(): TranscriptData[] {
    return this.localTranscriber?.transcripts || [];
  }

  /**
   * Check if recording is supported
   */
  static isRecordingSupported(): boolean {
    return (
      typeof MediaRecorder !== "undefined" &&
      typeof MediaRecorder.isTypeSupported === "function"
    );
  }

  /**
   * Check if transcription is supported
   */
  static isTranscriptionSupported(): boolean {
    return !!(
      window.SpeechRecognition || (window as any).webkitSpeechRecognition
    );
  }

  /**
   * Get supported mime types for recording
   */
  static getSupportedMimeTypes(): string[] {
    const mimeTypes = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=h264,opus",
      "video/webm",
      "video/mp4;codecs=h264,aac",
      "video/mp4",
    ];

    return mimeTypes.filter(
      (mimeType) =>
        MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(mimeType)
    );
  }

  /**
   * Cleanup recorder resources
   */
  cleanup(): void {
    console.log("[CallRecorder] Cleaning up recorder");

    if (this.isRecording) {
      this.stopRecording();
    }

    // Stop transcriber
    if (this.localTranscriber) {
      this.localTranscriber.stop();
      this.localTranscriber = null;
    }

    // Release mixer
    if (this.mixer) {
      this.mixer.releaseStreams();
      this.mixer = null;
    }

    this.recorder = null;
  }
}

// Extend Window interface for speech recognition
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}
